/* eslint-disable eqeqeq */
// notificationHandler.js
import { toast } from "react-toastify";
import { activeNotification } from "../Redux/Slices/notificationSlice";
import { toastProperties } from "../commons/Toast/Toaster";
import {
  addNewCounterPartyBid,
  addNewCounterPartyListing,
  emptyCounterPartyList,
  fetchActiveConnection,
  fetchCounterPartList,
  fetchLedgerBids,
  fetchSearchListing,
  fetchSelfCapacity,
  fetchSelfCapacityById,
  setActiveListingId,
  updateActiveConnection,
  updateBidStatus,
  updateCounterListingData,
  updateCounterListingStatus,
  updateMainScreenData,
  updateSelfCapacityById,
} from "../Redux/Slices/CapacitySlice";
import { store } from "../Redux/store";
import {
  getConnectionDetails,
  getMainScreenConnectionDetails,
  getSelfCounterPartyListings,
} from "../Services/BuySell/Listing";
//
import {
  addNewCounterPartyListingBanking,
  fetchAllBankingListings,
  fetchLedgerBanking,
  fetchMatchmakingResults,
  updateBankingBidStatus,
  updateMatchesListingData,
  updateBankingMatchmakingData,
} from "../Redux/Slices/bankingSlice";
import { getMatchmakingResults } from "../Services/ApiCalling/banking";

// A notification backlog delivers many notifications back-to-back, and each
// one used to dispatch its own fetchSelfCapacity — refetching the entire
// listing list, which in turn re-triggers per-listing contract-id fetches in
// SelfListing.jsx. Coalesce bursts into a single trailing refresh per user.
const SELF_CAPACITY_DEBOUNCE_MS = 800;
const selfCapacityDebounceTimers = new Map();

function debouncedFetchSelfCapacity(dispatch, user, showLoading) {
  return new Promise((resolve) => {
    const existing = selfCapacityDebounceTimers.get(user);
    if (existing) clearTimeout(existing.timer);

    const timer = setTimeout(() => {
      selfCapacityDebounceTimers.delete(user);
      Promise.resolve(dispatch(fetchSelfCapacity({ user, showLoading }))).then(
        resolve,
      );
    }, SELF_CAPACITY_DEBOUNCE_MS);

    selfCapacityDebounceTimers.set(user, { timer, resolve });
  });
}

async function refreshListingData(
  dispatch,
  state,
  notificationId,
  includeUpdateSelfCapacity = false,
  isClose = false,
) {
  const user = JSON.parse(
    JSON.stringify(state?.persistedReducer?.userType),
  ).toLowerCase();

  const stateName = state?.persistedReducer?.company?.stateLabel;

  await new Promise((resolve) => setTimeout(resolve, 2000));

  if (window.location.pathname.includes("/search_listing")) {
    const aa = user === "consumer" ? "generator" : "consumer";

    // Both intra/interstate share a single `searchListing` slot, so fetching
    // both in parallel would let whichever resolves last clobber the visible
    // list. Refresh only the tab the user is currently viewing.
    const currentListype = state?.CapacitySlice?.searchListype ?? false;

    await dispatch(
      fetchSearchListing({
        user: aa,
        curr: 0,
        type: currentListype,
        state: stateName,
        waitForBackend: true,
      }),
    );
    return;
  }

  if (includeUpdateSelfCapacity) {
    if (!notificationId) {
      await debouncedFetchSelfCapacity(dispatch, user, false);
      return;
    }
    // Parallel execution
    const [, activeConnectionData] = await Promise.all([
      dispatch(updateSelfCapacityById()),
      getConnectionDetails(0, notificationId, null),
    ]);

    dispatch(
      updateActiveConnection(
        activeConnectionData?.data?.data?.page ||
          activeConnectionData?.data?.data,
      ),
    );
  }

  if (notificationId) {
    const payload = {
      id: notificationId,
      energySourceType: "",
      energyFuelType: "",
      tag: "",
    };

    if (isClose) {
      dispatch(setActiveListingId(null));
      dispatch(emptyCounterPartyList());
    }

    // Execute independent calls in parallel
    const [mainscreenData] = await Promise.all([
      getMainScreenConnectionDetails(notificationId),
      dispatch(fetchSelfCapacityById({ user, id: notificationId })),
      dispatch(
        fetchCounterPartList({ user, payload, curr: 0, showLoading: false }),
      ),
      dispatch(fetchLedgerBids(notificationId, { showLoading: false })),
      debouncedFetchSelfCapacity(dispatch, user, false),
      dispatch(
        fetchActiveConnection({
          curr: 0,
          capId: notificationId,
          isTermsAccepted: null,
          showLoading: false,
        }),
      ),
    ]);

    dispatch(
      updateMainScreenData(
        mainscreenData?.data?.data?.page || mainscreenData?.data?.data,
      ),
    );
  }
}

async function handleConnectionRequestAccepted(
  dispatch,
  state,
  notificationId,
  data,
) {
  const activeListingId = state?.CapacitySlice?.activeListingId;
  if (String(activeListingId) !== String(notificationId)) return;

  dispatch(updateBidStatus(data?.data));
  await new Promise((resolve) => setTimeout(resolve, 2000));

  const mainscreenData = await getMainScreenConnectionDetails(activeListingId);
  dispatch(fetchLedgerBids(activeListingId));
  dispatch(
    updateMainScreenData(
      mainscreenData?.data?.data?.page || mainscreenData?.data?.data,
    ),
  );

  const payloadParams = {
    id: activeListingId,
    energySourceType: "",
    energyFuelType: "",
    tag: "",
  };
  const user = JSON.parse(
    JSON.stringify(state?.persistedReducer?.userType),
  ).toLowerCase();

  const counterData = await getSelfCounterPartyListings(user, payloadParams, 0);
  const payload =
    user === "consumer"
      ? counterData?.data?.data?.generatorList
      : counterData?.data?.data?.consumerList;

  dispatch(updateCounterListingData(payload));
}

async function refreshBankingMatchmakingData(dispatch, activeListingId) {
  try {
    const matchmakingRes = await getMatchmakingResults(activeListingId);

    if (matchmakingRes?.data?.success && matchmakingRes?.data?.data) {
      const freshData = {
        matches: matchmakingRes.data.data.matches || [],
        selfListing: matchmakingRes.data.data.selfListing || null,
        ...matchmakingRes.data.data,
      };

      dispatch(updateBankingMatchmakingData(freshData));
    } else {
    }
  } catch (error) {}
}

export async function handleNotification(dispatch, notification) {
  console.log("Notification received:", notification);
  const { key, url, title, message, reload, data } = notification;

  toast.info(`${message}`, {
    ...toastProperties,
    onClick: () => {
      if (url) {
        window.location.href = `${window.location.origin}${url}`;
      }
    },
  });

  dispatch(activeNotification({ key, url, title, message, reload, data }));
  const state = store.getState();
  const currentPath = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get("id");
  let storeActiveId = state?.CapacitySlice?.activeListingId;
  if (!storeActiveId) {
    if (
      window.location.pathname.includes("/bilateral") ||
      window.location.pathname.includes("/ledger") ||
      window.location.pathname.includes("/ongoing") ||
      window.location.pathname.includes("/banking_search")
    ) {
      const urlParams = new URLSearchParams(window.location.search);
      storeActiveId = urlParams.get("id");
    }
  }

  const activeListingId = state?.bankingSlice?.activeListingId;
  if (activeListingId) {
    dispatch(
      fetchMatchmakingResults({
        id: activeListingId,
        statusFilter: null,
      }),
    );
  }

  const notificationUrl = url || "";
  const notificationUrlParams = new URLSearchParams(
    notificationUrl.split("?")[1],
  );
  const notificationId = notificationUrlParams.get("id");

  let bankingListingId = null;

  if (notification.key?.startsWith("BANKING")) {
    if (notification.data?.listingId) {
      bankingListingId = notification.data.listingId;

      const listingMatch = bankingListingId.match(/B(\d+)_/);
      if (listingMatch) {
        bankingListingId = listingMatch[1];
      }
    } else if (notification.data?.id) {
      bankingListingId = notification.data.id;
    } else if (notification.listingId) {
      bankingListingId = notification.listingId;
    } else if (notificationId) {
      bankingListingId = notificationId;
    }
  }

  if (reload) {
    switch (notification?.key) {
      case "BSBPConnectionRequestSent":
        if (currentPath === "/ledger") {
          const urlParams = new URLSearchParams(window.location.search);
          const id = urlParams.get("id");
          dispatch(fetchLedgerBids(id));
        }
        break;
      case "BSBPConnectionRequestReceived":
        if (currentPath === "/ledger") {
          const urlParams = new URLSearchParams(window.location.search);
          const id = urlParams.get("id");
          dispatch(fetchLedgerBids(id));
        }

        break;

      case "BSBPTACShared":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPListingCreatedToAllOtherParties":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPContractClosureNotification":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true, true);
        }
        break;
      case "BSBPDocumentSubmitted":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPTACWithdrawn":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPTACUpdated":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPListingUpdated":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPListingOnHold":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPDocumentRejected":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(
            dispatch,
            state,
            storeActiveId || notificationId,
            true,
          );
        }
        break;
      case "BSBPDocumentUploadedToSelf":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(
            dispatch,
            state,
            storeActiveId || notificationId,
            true,
          );
        }
        break;

      case "BSBPTACSharedReject":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPTACAllAccepted":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;

      case "BSBPListingWithdrawn":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPLisitngWithdrawnToCreator":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPLisitngUpdatedToCreator":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPLisitngOnHoldToCreator":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;

      case "BSBPTACRejection":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;
      case "BSBPTACAccepted":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;

      case "BSBPDocumentApproved":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, notificationId);
        }
        break;
      case "BSBPGenerateConsent":
      case "BSBPGenerateConsentToCreator":
        const consentListingId =
          data?.counterPartyListingId || data?.id || notificationId;
        if (
          consentListingId &&
          (id == consentListingId ||
            String(storeActiveId) == String(consentListingId) ||
            id == notificationId ||
            String(storeActiveId) == String(notificationId))
        ) {
          await refreshListingData(
            dispatch,
            state,
            id || storeActiveId || consentListingId,
          );
        }
        break;
      case "BSBPConsentDocumentUploaded":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, notificationId);
        }
        break;
      case "BSBPLdcDocumentUploaded":
        if (
          id == notificationId ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, notificationId, true);
        }
        break;

      case "BSBPDocumentUploaded":
        if (
          ((data?.documentType === "Final Agreement" ||
            data?.documentType === "LDC" ||
            data?.documentType === "STOA") &&
            id == notificationId) ||
          String(storeActiveId) == String(notificationId)
        ) {
          await refreshListingData(dispatch, state, notificationId, true);
        }

        break;

      //Banking notification
      case "BANKINGListingCreatedToMatchingParties": {
        const matchmakingData = state?.bankingSlice?.matchmakingData;
        const activeListingId = state?.bankingSlice?.activeListingId;
        const activeListingName = matchmakingData?.selfListing?.name;
        const activeListingSelfId = matchmakingData?.selfListing?.id;

        const isMatchedWithActiveListing =
          data?.data?.matchedListingNames?.includes(activeListingName);

        const isActiveListingValid =
          activeListingId &&
          String(activeListingId) === String(activeListingSelfId);

        const isOnBankingPage = currentPath === "/banking";

        if (
          (activeListingId &&
            (String(activeListingId) === String(activeListingSelfId) ||
              data?.data?.matchedListingNames?.includes(activeListingName))) ||
          isOnBankingPage
        ) {
          dispatch(addNewCounterPartyListingBanking(data?.data?.listingData));

          if (
            isOnBankingPage &&
            isActiveListingValid &&
            isMatchedWithActiveListing
          ) {
            const currentUrl = new URL(window.location.href);
            const urlId = currentUrl.searchParams.get("id");
            if (String(urlId) !== String(activeListingId)) {
              currentUrl.searchParams.set("id", activeListingId);
              window.history.pushState({}, "", currentUrl.toString());
            }

            setTimeout(() => {
              window.location.reload();
            }, 5000);
            return;
          }

          if (activeListingId) {
            await refreshBankingMatchmakingData(dispatch, activeListingId);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        } else {
        }

        break;
      }
      case "BANKINGConnectionRequestReceived":
        const matchmakingDataReceived = state?.bankingSlice?.matchmakingData;
        const activeListingIdReceived = state?.bankingSlice?.activeListingId;
        const isOnBankingPageReceived =
          currentPath === "/banking" || currentPath === "/ledger";

        if (matchmakingDataReceived && data?.data) {
          const matchingBid = matchmakingDataReceived?.matches?.find(
            (match) => match.connectId === data.data.connectId,
          );

          if (
            matchingBid ||
            String(activeListingIdReceived) === String(data.data.id) ||
            matchmakingDataReceived?.selfListing?.id === data.data.id ||
            (isOnBankingPageReceived && activeListingIdReceived)
          ) {
            dispatch(
              updateMatchesListingData({
                connectId: data.data.connectId,
                status: data.data.status,
                id: data.data.id,
              }),
            );

            const listingIdToRefresh =
              activeListingIdReceived || bankingListingId;
            if (listingIdToRefresh) {
              await refreshBankingMatchmakingData(dispatch, listingIdToRefresh);
            }

            dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
            dispatch(fetchAllBankingListings());
          }
        }
        break;

      case "BANKINGConnectionRequestAccepted": {
        const activeListingIdForAccepted = state?.bankingSlice?.activeListingId;
        if (
          id === bankingListingId ||
          String(activeListingIdForAccepted) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse = activeListingIdForAccepted || bankingListingId;

          const isOnBankingPage =
            currentPath === "/banking" ||
            currentPath === "/banking_active" ||
            currentPath.includes("/banking");

          const shouldReload =
            isOnBankingPage &&
            (String(activeListingIdForAccepted) === String(bankingListingId) ||
              String(id) === String(bankingListingId));

          if (shouldReload) {
            const currentUrl = new URL(window.location.href);
            const urlId = currentUrl.searchParams.get("id");

            if (String(urlId) !== String(bankingListingId)) {
              currentUrl.searchParams.set("id", bankingListingId);
              window.history.pushState({}, "", currentUrl.toString());
            }

            setTimeout(() => {
              window.location.reload();
            }, 2000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }
          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        } else {
        }

        break;
      }

      case "BANKINGTACShared":
        const activeListingIdForTAC = state?.bankingSlice?.activeListingId;

        if (
          id === bankingListingId ||
          String(activeListingIdForTAC) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse = activeListingIdForTAC || bankingListingId;

          if (String(activeListingIdForTAC) === String(bankingListingId)) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 3000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }
          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }

        break;

      case "BANKINGTACAccepted":
        const activeListingIdTACAccepted = state?.bankingSlice?.activeListingId;
        if (
          id === bankingListingId ||
          String(activeListingIdTACAccepted) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse = activeListingIdTACAccepted || bankingListingId;
          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }
        break;

      case "BANKINGTACDeclined":
        const activeListingIdTACDeclined = state?.bankingSlice?.activeListingId;
        if (
          id === bankingListingId ||
          String(activeListingIdTACDeclined) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse = activeListingIdTACDeclined || bankingListingId;

          if (String(activeListingIdTACDeclined) === String(bankingListingId)) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 3000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }
          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }
        break;

      case "BANKINGDocumentApproved":
        const activeListingIdDocApproved = state?.bankingSlice?.activeListingId;
        if (
          id === bankingListingId ||
          String(activeListingIdDocApproved) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse = activeListingIdDocApproved || bankingListingId;

          if (String(activeListingIdDocApproved) === String(bankingListingId)) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 5000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }
        break;

      case "BANKINGDocumentRejected":
        const activeListingIdDocRejected = state?.bankingSlice?.activeListingId;
        if (
          id === bankingListingId ||
          String(activeListingIdDocRejected) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse = activeListingIdDocRejected || bankingListingId;

          if (String(activeListingIdDocRejected) === String(bankingListingId)) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 5000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }
        break;

      case "BANKINGDocumentUploaded":
        if (
          data?.documentType === "Final Agreement" ||
          data?.documentType === "LDC" ||
          data?.documentType === "STOA"
        ) {
          const activeListingIdDocUploaded =
            state?.bankingSlice?.activeListingId;

          if (
            id === bankingListingId ||
            String(activeListingIdDocUploaded) === String(bankingListingId) ||
            bankingListingId
          ) {
            const listingIdToUse =
              activeListingIdDocUploaded || bankingListingId;

            if (
              String(activeListingIdDocUploaded) === String(bankingListingId)
            ) {
              const currentUrl = new URL(window.location.href);
              currentUrl.searchParams.set("id", bankingListingId);
              window.history.pushState({}, "", currentUrl.toString());

              // setTimeout(() => {
              //   window.location.reload();
              // }, 5000);
              return;
            }

            if (listingIdToUse) {
              await refreshBankingMatchmakingData(dispatch, listingIdToUse);
            }

            dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
            dispatch(fetchAllBankingListings());
          }
        }
        break;

      case "BANKINGConnectionRequestDeclined":
        // case "BANKINGConnectionRequestDeclinedSelf":
        const activeListingIdForListingDec =
          state?.bankingSlice?.activeListingId;
        if (
          id === bankingListingId ||
          String(activeListingIdForListingDec) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse =
            activeListingIdForListingDec || bankingListingId;

          if (
            String(activeListingIdForListingDec) === String(bankingListingId)
          ) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 3000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }

        break;
      case "BANKINGListingOnHold":
      case "BANKINGListingUpdated":
      case "BANKINGListingWithdrawn":
      case "BANKINGListingUpdatedToCreator":
        const activeListingIdForListing = state?.bankingSlice?.activeListingId;
        if (
          id === bankingListingId ||
          String(activeListingIdForListing) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse = activeListingIdForListing || bankingListingId;

          if (String(activeListingIdForListing) === String(bankingListingId)) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 3000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }
        break;
      case "BANKINGLedgerUpdated":
        const activeListingIdLedgerUpdated =
          state?.bankingSlice?.activeListingId;

        if (
          id === bankingListingId ||
          String(activeListingIdLedgerUpdated) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse =
            activeListingIdLedgerUpdated || bankingListingId;

          if (
            String(activeListingIdLedgerUpdated) === String(bankingListingId)
          ) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 3000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }
        break;
      case "BANKINGLedgerRejected":
        const activeListingIdLedgerRejected =
          state?.bankingSlice?.activeListingId;

        if (
          id === bankingListingId ||
          String(activeListingIdLedgerRejected) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse =
            activeListingIdLedgerRejected || bankingListingId;

          if (
            String(activeListingIdLedgerRejected) === String(bankingListingId)
          ) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 3000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }
        break;
      case "BANKINGLedgerApproved":
        const activeListingIdLedgerApproved =
          state?.bankingSlice?.activeListingId;

        if (
          id === bankingListingId ||
          String(activeListingIdLedgerApproved) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse =
            activeListingIdLedgerApproved || bankingListingId;

          if (
            String(activeListingIdLedgerApproved) === String(bankingListingId)
          ) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 3000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }
        break;
      case "BANKINGLedgerCreated":
        const activeListingIdLedgerCreated =
          state?.bankingSlice?.activeListingId;

        if (
          id === bankingListingId ||
          String(activeListingIdLedgerCreated) === String(bankingListingId) ||
          bankingListingId
        ) {
          const listingIdToUse =
            activeListingIdLedgerCreated || bankingListingId;

          if (
            String(activeListingIdLedgerCreated) === String(bankingListingId)
          ) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set("id", bankingListingId);
            window.history.pushState({}, "", currentUrl.toString());

            setTimeout(() => {
              window.location.reload();
            }, 1000);
            return;
          }

          if (listingIdToUse) {
            await refreshBankingMatchmakingData(dispatch, listingIdToUse);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        }
        break;
      default:
        break;
    }
  } else {
    switch (notification?.key) {
      case "BSBPConnectionRequestAccepted":
        await handleConnectionRequestAccepted(
          dispatch,
          state,
          notificationId,
          data,
        );
        break;
      case "BSBPBidWithdrawn":
        await handleConnectionRequestAccepted(
          dispatch,
          state,
          notificationId,
          data,
        );
        break;
      case "BSBPConnectionRequestDeclined":
        await handleConnectionRequestAccepted(
          dispatch,
          state,
          notificationId,
          data,
        );
        break;
      case "BSBPConnectionRequestReceived":
        const consumerName =
          data?.data?.consumerName || data?.data?.generatorName || "";

        if (
          store.getState()?.CapacitySlice?.activeMain?.name === consumerName
        ) {
          dispatch(
            updateCounterListingStatus({
              id: data?.data?.consumer?.id || data?.data?.generator?.id,
              status: data?.data?.status || data?.data?.status,
            }),
          );

          dispatch(addNewCounterPartyBid(data?.data));
        }

        // Refresh the available-side (main screen) data whenever the request
        // targets the listing the user is currently viewing. The name guard
        // above is skipped for this payload (consumerName/generatorName are
        // not present on data.data), so gate the refresh on the listing id
        // instead — matching how the rest of the handler resolves the active
        // listing.
        if (
          notificationId &&
          (id == notificationId ||
            String(storeActiveId) == String(notificationId))
        ) {
          const mainscreenData =
            await getMainScreenConnectionDetails(notificationId);
          dispatch(
            updateMainScreenData(
              mainscreenData?.data?.data?.page || mainscreenData?.data?.data,
            ),
          );
        }
        break;
      case "BSBPListingCreatedToMatchingParties":
        if (
          data?.data?.matchedListingNames?.includes(
            state?.CapacitySlice?.activeMain?.name,
          ) &&
          currentPath === "/dashboard"
        ) {
          dispatch(addNewCounterPartyListing(data?.data?.createdListing));
          // await refreshListingData(dispatch, state, storeActiveId, true);
        } else if (currentPath === "/search_listing") {
          await refreshListingData(dispatch, state, storeActiveId, true);
        }
        break;

      //banking notification
      case "BANKINGListingCreatedToMatchingParties": {
        const matchmakingData = state?.bankingSlice?.matchmakingData;
        const activeListingId = state?.bankingSlice?.activeListingId;
        const activeListingName = matchmakingData?.selfListing?.name;
        const activeListingSelfId = matchmakingData?.selfListing?.id;

        if (
          (activeListingId &&
            (String(activeListingId) === String(activeListingSelfId) ||
              data?.data?.matchedListingNames?.includes(activeListingName))) ||
          currentPath === "/banking"
        ) {
          dispatch(addNewCounterPartyListingBanking(data?.data?.listingData));

          // Refresh matchmaking data to get updated listings
          if (activeListingId) {
            await refreshBankingMatchmakingData(dispatch, activeListingId);
          }

          dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
          dispatch(fetchAllBankingListings());
        } else {
        }

        break;
      }
      case "BANKINGConnectionRequestReceived":
        const matchmakingDataReceived = state?.bankingSlice?.matchmakingData;
        const activeListingIdReceived = state?.bankingSlice?.activeListingId;
        const isOnBankingPageReceived =
          currentPath === "/banking" || currentPath === "/ledger";

        if (matchmakingDataReceived && data?.data) {
          const matchingBid = matchmakingDataReceived?.matches?.find(
            (match) => match.connectId === data.data.connectId,
          );

          if (
            matchingBid ||
            String(activeListingIdReceived) === String(data.data.id) ||
            matchmakingDataReceived?.selfListing?.id === data.data.id ||
            (isOnBankingPageReceived && activeListingIdReceived)
          ) {
            dispatch(
              updateMatchesListingData({
                connectId: data.data.connectId,
                status: data.data.status,
                id: data.data.id,
              }),
            );

            if (activeListingIdReceived) {
              await refreshBankingMatchmakingData(
                dispatch,
                activeListingIdReceived,
              );
            }
          }
        }
        break;

      case "BANKINGConnectionRequestAccepted":
        const matchmakingDataAAccepted = state?.bankingSlice?.matchmakingData;
        const activeListingIdAccepted = state?.bankingSlice?.activeListingId;
        const isOnBankingPageAccepted =
          currentPath === "/banking" || currentPath === "/ledger";

        if (matchmakingDataAAccepted && data?.data) {
          const matchingBid = matchmakingDataAAccepted?.matches?.find(
            (match) => match.connectId === data.data.connectId,
          );

          if (
            matchingBid ||
            String(activeListingIdAccepted) === String(data.data.id) ||
            matchmakingDataAAccepted?.selfListing?.id === data.data.id ||
            (isOnBankingPageAccepted && activeListingIdAccepted)
          ) {
            // First update the specific bid status
            dispatch(
              updateBankingBidStatus({
                connectId: data.data.connectId,
                status: data.data.status,
                id: data.data.id,
              }),
            );

            // Then use simple refresh for data update
            const listingIdToRefresh =
              activeListingIdAccepted || bankingListingId;
            if (listingIdToRefresh) {
              await refreshBankingMatchmakingData(dispatch, listingIdToRefresh);
            }

            // Refresh other banking data
            dispatch(fetchLedgerBanking({ currentPage: 0, size: 20 }));
            dispatch(fetchAllBankingListings());
          }
        }
        break;

      default:
        break;
    }
  }
}
